import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Sparkles, Search, MapPin, Briefcase, DollarSign, User } from 'lucide-react';
import './AISearch.scss';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ParsedFilter {
  key: string;
  label: string;
  value: string;
  /** Raw URL param name used by /skelbimai */
  urlParam: string;
  icon: React.ReactNode;
}

// ─── AI parser (mock) ────────────────────────────────────────────────────────

const CITY_MAP: Record<string, string> = {
  kaune: 'Kaunas', kaunas: 'Kaunas', vilniuje: 'Vilnius', vilnius: 'Vilnius',
  klaipėdoje: 'Klaipėda', klaipėda: 'Klaipėda', šiauliuose: 'Šiauliai', šiauliai: 'Šiauliai',
  panevėžyje: 'Panevėžys', panevėžys: 'Panevėžys', alytuje: 'Alytus', alytus: 'Alytus',
};

const FIELD_MAP: Record<string, string> = {
  statybos: 'Statyba', statyba: 'Statyba', statybai: 'Statyba', statybų: 'Statyba',
  statybininkas: 'Statyba', statybininku: 'Statyba',
  santechnikas: 'Santechnika', santechnikų: 'Santechnika', santechniko: 'Santechnika',
  elektrikas: 'Elektra', elektriko: 'Elektra',
  suvirintojas: 'Suvirinimas', suvirintojo: 'Suvirinimas',
  dailidė: 'Medienos apdirbimas', stogdengys: 'Statyba', stogdengio: 'Statyba',
  tinkuotojas: 'Statyba', mūrininkas: 'Statyba',
  vairuotojas: 'Transportas', vairuotojo: 'Transportas',
  buhalteris: 'Buhalterija', buhalterio: 'Buhalterija',
  programuotojas: 'IT', vadybininkas: 'Vadyba', vadybininko: 'Vadyba',
};

const ROLE_MAP: Record<string, string> = {
  darbininkų: 'Darbininkas', darbininkus: 'Darbininkas', darbininkas: 'Darbininkas',
  darbininku: 'Darbininkas', darbininko: 'Darbininkas',
  meistras: 'Meistras', meistrą: 'Meistras', meistro: 'Meistras',
  brigadininkas: 'Brigadininkas', brigadininko: 'Brigadininkas',
  inžinierius: 'Inžinierius', inžinieriaus: 'Inžinierius',
  vadovas: 'Vadovas', vadovo: 'Vadovas',
  specialistas: 'Specialistas', specialisto: 'Specialistas',
  santechnikas: 'Santechnikas', santechniko: 'Santechnikas',
  elektrikas: 'Elektrikas', elektriko: 'Elektrikas',
};

function parseQuery(query: string): ParsedFilter[] {
  const lower = query.toLowerCase().replace(/[,.!?]/g, ' ');
  const words = lower.split(/\s+/);
  const filters: ParsedFilter[] = [];

  for (const w of words) {
    if (CITY_MAP[w]) {
      filters.push({ key: 'location', label: 'Miestas', value: CITY_MAP[w], urlParam: 'location', icon: <MapPin size={14} /> });
      break;
    }
  }

  for (const w of words) {
    if (FIELD_MAP[w]) {
      filters.push({ key: 'workFields', label: 'Darbo sritis', value: FIELD_MAP[w], urlParam: 'workFields', icon: <Briefcase size={14} /> });
      break;
    }
  }

  for (const w of words) {
    if (ROLE_MAP[w]) {
      filters.push({ key: 'positions', label: 'Pareigybės tipas', value: ROLE_MAP[w], urlParam: 'positions', icon: <User size={14} /> });
      break;
    }
  }

  const salaryMatch = query.match(/(\d[\d\s]*(?:€|eur(?:o|ų)?)|(?:€|eur(?:o|ų)?)\s*\d[\d\s]*|moku\s+(\d+))/i);
  if (salaryMatch) {
    const numStr = salaryMatch[0].replace(/[^\d]/g, '');
    const amount = parseInt(numStr, 10);
    if (amount > 100 && amount < 50000) {
      filters.push({ key: 'salaryMin', label: 'Atlyginimas', value: `iki ${amount.toLocaleString('lt-LT')} €`, urlParam: 'salaryMin', icon: <DollarSign size={14} /> });
    }
  }

  return filters;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PARSING_STEPS = [
  'Analizuojamas tekstas...',
  'Atpažįstama vieta...',
  'Nustatoma darbo sritis...',
  'Konfigūruojami filtrai...',
  'Ieškoma kandidatų...',
];

const EXAMPLE_PROMPTS = [
  'Reikia darbininkų Kaune namo statybai moku 2000€',
  'Ieškau santechniko Vilniuje, alga iki 1800€',
  'Reikia brigadininko Klaipėdoje, galiu mokėti 2500€',
  'Ieškau elektriko Šiauliuose, moku 1600€',
];

// ─── Component ───────────────────────────────────────────────────────────────

type SearchState = 'idle' | 'parsing';

const AISearch: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [state, setState] = useState<SearchState>('idle');
  const [parsedFilters, setParsedFilters] = useState<ParsedFilter[]>([]);
  const [parsingStep, setParsingStep] = useState(0);
  const stepRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleSearch = () => {
    if (!query.trim()) return;

    const filters = parseQuery(query);
    setParsedFilters(filters);
    setState('parsing');
    setParsingStep(0);

    let step = 0;
    stepRef.current = setInterval(() => {
      step += 1;
      setParsingStep(step);

      if (step >= PARSING_STEPS.length - 1) {
        clearInterval(stepRef.current!);

        setTimeout(() => {
          // Build URL params for /skelbimai
          const params = new URLSearchParams();
          params.set('ai', '1');
          params.set('aiQuery', query.trim());
          for (const f of filters) {
            // salary stored as raw number for /skelbimai
            if (f.urlParam === 'salaryMin') {
              const num = f.value.replace(/[^\d]/g, '');
              params.set('salaryMin', num);
            } else {
              params.set(f.urlParam, f.value);
            }
          }
          navigate(`/skelbimai?${params.toString()}`);
        }, 400);
      }
    }, 320);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <div className="ai-search-page">
      <div className="container">

        {/* Breadcrumbs */}
        <div className="breadcrumbs">
          <Link to="/">Titulinis</Link>
          <ChevronRight size={14} />
          <span>AI Paieška</span>
        </div>

        {/* Hero */}
        <div className="ai-hero">
          <div className="ai-hero__badge">
            <Sparkles size={16} />
            <span>Dirbtinis intelektas</span>
          </div>
          <h1 className="ai-hero__title">AI CV Paieška</h1>
          <p className="ai-hero__subtitle">
            Įveskite, ko ieškote laisvu tekstu — AI automatiškai nustatys filtrus ir pateiks tinkamiausius kandidatus.
          </p>
        </div>

        {/* Search box */}
        <div className="ai-search-box">
          <div className="ai-search-box__inner">
            <Sparkles size={20} className="ai-search-box__icon" />
            <textarea
              className="ai-search-box__input"
              placeholder={'pvz. \u201EReikia darbinik\u0173 Kaune namo statybai moku 2000\u20AC\u201C'}
              value={query}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={2}
              disabled={state === 'parsing'}
            />
          </div>
          <div className="ai-search-box__footer">
            <div className="ai-search-box__examples">
              <span className="ai-search-box__examples-label">Pavyzdžiai:</span>
              {EXAMPLE_PROMPTS.map((p, i) => (
                <button
                  key={i}
                  className="ai-search-box__example-chip"
                  onClick={() => setQuery(p)}
                  disabled={state === 'parsing'}
                >
                  {p}
                </button>
              ))}
            </div>
            <div className="ai-search-box__actions">
              <button
                className="btn-primary ai-search-box__submit"
                onClick={handleSearch}
                disabled={!query.trim() || state === 'parsing'}
              >
                {state === 'parsing' ? (
                  <>
                    <span className="spinner-sm" />
                    Analizuojama...
                  </>
                ) : (
                  <>
                    <Search size={16} />
                    Ieškoti su AI
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Parsing animation */}
        {state === 'parsing' && (
          <div className="ai-parsing">
            {parsedFilters.length > 0 && (
              <div className="ai-parsing__preview">
                <span className="ai-parsing__preview-label">Aptikta:</span>
                {parsedFilters.map(f => (
                  <span key={f.key} className="ai-parsing__preview-chip">
                    {f.icon}
                    <strong>{f.label}:</strong> {f.value}
                  </span>
                ))}
              </div>
            )}
            <div className="ai-parsing__steps">
              {PARSING_STEPS.map((step, i) => (
                <div
                  key={i}
                  className={[
                    'ai-parsing__step',
                    i <= parsingStep ? 'ai-parsing__step--active' : '',
                    i < parsingStep  ? 'ai-parsing__step--done'   : '',
                  ].join(' ').trim()}
                >
                  <div className="ai-parsing__step-dot" />
                  <span>{step}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Idle how-it-works */}
        {state === 'idle' && (
          <div className="ai-idle-state">
            <div className="ai-idle-state__icon">
              <Sparkles size={48} />
            </div>
            <h3>Kaip tai veikia?</h3>
            <div className="ai-idle-state__steps">
              <div className="ai-idle-state__step">
                <div className="ai-idle-state__step-num">1</div>
                <div>
                  <strong>Aprašykite poreikį</strong>
                  <p>Įveskite, ko ieškote, laisvu tekstu — tarsi rašytumėte draugui.</p>
                </div>
              </div>
              <div className="ai-idle-state__step">
                <div className="ai-idle-state__step-num">2</div>
                <div>
                  <strong>AI analizuoja</strong>
                  <p>Algoritmas atpažįsta miestą, darbo sritį, atlyginimą ir kitus kriterijus.</p>
                </div>
              </div>
              <div className="ai-idle-state__step">
                <div className="ai-idle-state__step-num">3</div>
                <div>
                  <strong>Gaunate kandidatus</strong>
                  <p>Automatiškai atidaromas kandidatų sąrašas su pritaikytais filtrais — galite juos tikslinti.</p>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AISearch;
